import { searchShowOnMapClick } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import TextLink from 'LuxKit/TextLink'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatInlineList from 'components/utils/Formatters/FormatInlineList'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import { trackClientEvent } from 'analytics/analytics'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import SearchTargetDistance from 'components/OfferList/OfferListTiles/Support/SearchTargetDistance'

interface Props {
  locations: Array<string>
  mapUrl?: string
  distance?: number
  searchTargetLabel?: string
}

function SearchTileRowOverlineLocation({ locations, mapUrl, distance, searchTargetLabel }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { isHomesAndVillasSplitMapVisible } = useContext(GlobalSearchStateContext)

  const showOnMapLabel = intl.formatMessage({
    id: '9llOWZPByHFkZa27u2zT',
    defaultMessage: 'Show on map',
    description: 'Link text and aria-label to show hotel location on map in search tile',
  })
  const middleDotLabel = intl.formatMessage(middleDotSeparatorMessage)

  const mapViewClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.stopPropagation()
    event.preventDefault()

    trackClientEvent({
      subject: 'show_on_map',
      action: 'clicked',
      type: 'interaction',
      category: 'vertical_page',
    })

    addGTMEvent(searchShowOnMapClick())

    if (mapUrl) {
      let destinationUrl = mapUrl

      const isHomesAndVillasMapRoute = mapUrl.startsWith('/search/homes-and-villas/map?')

      if (isHomesAndVillasMapRoute && isHomesAndVillasSplitMapVisible) {
        destinationUrl = mapUrl.replace('/search/homes-and-villas/map?', '/search/homes-and-villas?')
      }

      dispatch(pushWithRegion(destinationUrl))
    }
  }, [mapUrl, dispatch, isHomesAndVillasSplitMapVisible])

  return <>
    <BodyText variant="small" colour="neutral-two">
      <FormatInlineList list={locations} type="unit" />
      {!!mapUrl && (
        <FormattedMessage
          id="o2ZRAxWmdsD2Fj2ukIzq"
          defaultMessage="<separator>{middleDotLabel}</separator><link>{showOnMapLabel}</link>"
          description="Middle dot separator followed by the show on map link in the search tile overline"
          values={{
            middleDotLabel,
            showOnMapLabel,
            separator: (chunks: React.ReactNode) => <span key="map-separator" style={{ marginInline: rem(4) }}>{chunks}</span>,
            link: (chunks: React.ReactNode) => <TextLink
              weight="regular"
              onClick={mapViewClick}
              role="link"
              data-href={mapUrl}
              aria-label={showOnMapLabel}
            >
              {chunks}
            </TextLink>,
          }}
        />
      )}
    </BodyText>
    {!!distance && !!searchTargetLabel && <SearchTargetDistance
      distance={distance}
      searchTargetLabel={searchTargetLabel}
    />}
  </>
}

export default SearchTileRowOverlineLocation
